import { DEFAULT_MARKDOWN_PLACEHOLDER } from '../constants'
import { basePropertiesSchema } from '../base-schema-properties'
import {
  bgColorSchema,
  colsNumberOnDesktopSchema,
  colsNumberOnMobileSchema,
  linkColorSchema,
  shadowSchema,
  showAllSettingsSchema,
  textColorSchema,
  titlePropertiesSchema,
  textFontFamilySchema,
} from '../common'

const CARD_PADDING_VALUES = ['none', 'xs', 'sm', 'md', 'lg']
const CARD_PADDING_NAMES = ['None', 'Extra small', 'Small', 'Medium', 'Large']

const getCardPaddingSchema = (
  title: string,
  defaultValue: string,
  axis: 'horizontal' | 'vertical'
) => ({
  title,
  type: 'string',
  enum: [...CARD_PADDING_VALUES, 'statistics-card'],
  enumNames: [
    ...CARD_PADDING_NAMES,
    axis === 'horizontal'
      ? 'Statistics card horizontal padding'
      : 'Statistics card vertical padding',
  ],
  default: defaultValue,
})

export default {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Statistics',
  description: 'Block with statistics cards',
  type: 'object',
  required: ['text', 'colsNumberOnDesktop', 'colsNumberOnMobile'],
  'ui:options': {
    onlyShowIfDependent: true,
  },
  dependencies: {
    tmp_showAllSettings: [
      'textColor',
      'bgColor',
      'cardTextColor',
      'cardBgColor',
      'linkColor',
      'blockShadow',
    ],
    title: ['titleAlign', 'hideOnEmbed'],
  },
  properties: {
    ...basePropertiesSchema,
    ...titlePropertiesSchema,
    ...showAllSettingsSchema,
    ...textColorSchema,
    textFontFamily: {
      ...textFontFamilySchema.textFontFamily,
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    ...linkColorSchema,
    ...bgColorSchema,
    ...shadowSchema,
    cardTextColor: {
      title: 'Card Text Color',
      type: 'string',
      default: '#5818f4',
    },
    cardBgColor: {
      title: 'Card Background Color',
      type: 'string',
      default: '#eee8fe',
    },
    text: {
      title: 'Text',
      type: 'string',
      'ui:options': {
        placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
        type: 'textarea',
        rows: 6,
      },
    },
    sideContentType: {
      title: 'Side content',
      type: 'string',
      enum: ['image', 'cards'],
      enumNames: ['Image', 'Text cards'],
      default: 'image',
      'ui:widget': 'RadioWidget',
    },
    image: {
      title: 'Image',
      type: 'string',
      'ui:hidden': '{{parentFormData.sideContentType === "cards"}}',
    },
    sideCardsContainerPaddingTopDesktop: {
      title: 'Cards container top padding on desktop, px',
      type: 'number',
      default: 0,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsContainerPaddingTopMobile: {
      title: 'Cards container top padding on mobile, px',
      type: 'number',
      default: 0,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsContainerPaddingBottomDesktop: {
      title: 'Cards container bottom padding on desktop, px',
      type: 'number',
      default: 0,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsContainerPaddingBottomMobile: {
      title: 'Cards container bottom padding on mobile, px',
      type: 'number',
      default: 0,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsContainerPaddingHorizontalDesktop: {
      title: 'Cards container horizontal padding on desktop, px',
      type: 'number',
      default: 0,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsContainerPaddingHorizontalMobile: {
      title: 'Cards container horizontal padding on mobile, px',
      type: 'number',
      default: 0,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsGapDesktop: {
      title: 'Gap between text cards on desktop, px',
      type: 'number',
      default: 16,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCardsGapMobile: {
      title: 'Gap between text cards on mobile, px',
      type: 'number',
      default: 16,
      minimum: 0,
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
    },
    sideCards: {
      type: 'array',
      title: 'A list of text cards',
      'ui:hidden': '{{parentFormData.sideContentType !== "cards"}}',
      'ui:options': {
        showIndexNumber: true,
      },
      items: {
        title: 'Text card',
        type: 'object',
        required: ['text'],
        properties: {
          text: {
            title: 'Text',
            type: 'string',
            default: '#### **Card title**\n\nCard text',
            'ui:options': {
              placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
              type: 'textarea',
              rows: 4,
            },
          },
          bgColor: {
            title: 'Background color',
            type: 'string',
            default: '#eee8fe',
          },
          paddingHorizontalDesktop: getCardPaddingSchema(
            'Horizontal padding on desktop',
            'md',
            'horizontal'
          ),
          paddingHorizontalMobile: getCardPaddingSchema(
            'Horizontal padding on mobile',
            'md',
            'horizontal'
          ),
          paddingVerticalDesktop: getCardPaddingSchema(
            'Vertical padding on desktop',
            'sm',
            'vertical'
          ),
          paddingVerticalMobile: getCardPaddingSchema(
            'Vertical padding on mobile',
            'sm',
            'vertical'
          ),
          borderRadius: {
            title: 'Border radius',
            type: 'string',
            enum: [
              'rounded-none',
              'rounded',
              'rounded-lg',
              'rounded-xl',
              'rounded-2xl',
              'rounded-3xl',
            ],
            enumNames: [
              'None (0 px)',
              'Default (4 px)',
              'Large (8 px)',
              'XL (12 px, default)',
              '2XL (16 px)',
              '3XL (24 px)',
            ],
            default: 'rounded-xl',
          },
        },
      },
    },
    imagePosition: {
      title: 'Side content position',
      type: 'string',
      enum: ['left', 'right'],
      enumNames: ['Left', 'Right'],
      default: 'right',
      'ui:hidden':
        '{{parentFormData.sideContentType !== "cards" && !parentFormData.image}}',
    },
    imageSize: {
      title: 'Side content width',
      type: 'string',
      enum: ['w-1/3', 'w-2/3', 'w-1/2'],
      enumNames: ['1/3', '2/3', '1/2'],
      default: 'w-1/3',
      'ui:hidden':
        '{{parentFormData.sideContentType !== "cards" && !parentFormData.image}}',
    },
    statCards: {
      type: 'array',
      title: 'A list of statistic cards',
      'ui:options': {
        showIndexNumber: true,
      },
      items: {
        title: 'Card info',
        type: 'object',
        required: ['title', 'subtitle'],
        properties: {
          title: {
            title: 'Title',
            type: 'string',
            default: '500K',
            'ui:width': '50%',
            maxLength: 4,
          },
          subtitle: {
            title: 'Subtitle',
            type: 'string',
            default: 'candidates in our talent pool',
            'ui:width': '50%',
          },
        },
      },
    },
    ...colsNumberOnDesktopSchema,
    ...colsNumberOnMobileSchema,
  },
}
